/**
 * External API queries for TanStack Query (Vendor App)
 * Includes: CEP lookup, states, cities
 */

import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet } from '../utils/request'

// Type definitions
export interface CEPResult {
  cep: string
  logradouro: string
  complemento: string
  bairro: string
  localidade: string
  uf: string
  ibge: string
  gia: string
  ddd: string
  siafi: string
}

export interface City {
  id: number
  nome: string
  codigo_ibge: string
}

// Cache configuration
const CACHE_TIMES = {
  CEP: 60 * 60 * 1000, // 1 hour - CEP data is static
  CITIES: 24 * 60 * 60 * 1000, // 24 hours - Cities rarely change
}


/**
 * Query hook to lookup a CEP (Brazilian postal code)
 */
export const useCEPQuery = (cep: MaybeRefOrGetter<string | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const cepValue = toValue(cep)
      return cepValue ? queryKeys.cep(cepValue) : ['external', 'cep', 'null']
    }),
    queryFn: async (): Promise<CEPResult> => {
      const cepValue = toValue(cep)
      if (!cepValue) throw new Error('CEP is required')
      // Clean CEP to only digits
      const cleanCEP = cepValue.replace(/\D/g, '')
      return queryGet<CEPResult>(`/v2/external/cep/${cleanCEP}`)
    },
    staleTime: CACHE_TIMES.CEP,
    enabled: computed(() => {
      const cepValue = toValue(cep)
      return !!cepValue && cepValue.replace(/\D/g, '').length === 8
    }),
  })
}

